import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Checkbox, Tag } from "antd";
import { RequirementConfirmShowEnum, RequirementConfirmTypeEnum } from "@/types/requirement";
import { useTranslation } from "react-i18next";

export interface RequirementConfirmLabel {
  name: string;
  type: RequirementConfirmTypeEnum;
  sort: number;
  show: RequirementConfirmShowEnum;
}

interface StepsFormItemProps {
  disableItems?: Array<RequirementConfirmTypeEnum>; // 禁止选择与拖拽项目
  greyItems?: Array<RequirementConfirmTypeEnum>; //不选择，不拖拽
  value?: Array<RequirementConfirmLabel>;
  onChange?: (value: Array<RequirementConfirmLabel>) => void;
}

const sorts = [1, 2, 3, 4, 5, 6]

const StepsFormItem: React.FC<StepsFormItemProps> = ({ value, onChange, disableItems = [RequirementConfirmTypeEnum.MATERIAL_CONTROL, RequirementConfirmTypeEnum.SALE], greyItems = [] }) => {
  const { t } = useTranslation();

  let defaultSteps = [
    {
      type: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      name: t('requirement.material'),
      show: RequirementConfirmShowEnum.SHOW,
    },
    {
      type: RequirementConfirmTypeEnum.COMMODITY,
      name: t('requirement.commodity'),
      show: RequirementConfirmShowEnum.HIDDEN,
    },
    {
      type: RequirementConfirmTypeEnum.PROD,
      name: t('requirement.prod'),
      show: RequirementConfirmShowEnum.SHOW,
    },
    {
      type: RequirementConfirmTypeEnum.PURCHASE,
      name: t('requirement.purchase'),
      show: RequirementConfirmShowEnum.SHOW,
    },
    {
      type: RequirementConfirmTypeEnum.FINANCE,
      name: t('requirement.finance'),
      show: RequirementConfirmShowEnum.SHOW,
    },
    {
      type: RequirementConfirmTypeEnum.SALE,
      name: t('requirement.sale'),
      show: greyItems.includes(RequirementConfirmTypeEnum.SALE) ? RequirementConfirmShowEnum.HIDDEN : RequirementConfirmShowEnum.SHOW,
    },
  ].map((item, index) => ({ ...item, sort: sorts[index] }))

  const [steps, setSteps] = useState<RequirementConfirmLabel[]>(value || defaultSteps);

  useEffect(() => {
    if (!value || value.length === 0) {
      setSteps(defaultSteps);
      onChange?.(defaultSteps);
    } else {
      setSteps(value);
    }
  }, [value, onChange]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    if (result.source.index === 0 || result.destination.index === 0) {
      return;
    }

    const reorderedSteps = Array.from(steps);
    const [removed] = reorderedSteps.splice(result.source.index, 1);
    reorderedSteps.splice(result.destination.index, 0, removed);

    const updatedSteps = reorderedSteps.map((step, index) => ({
      ...step,
      sort: index + 1,
    }));

    setSteps(updatedSteps);
    onChange?.(updatedSteps);
  };

  const toggleShow = (type: RequirementConfirmTypeEnum) => {
    const updatedSteps = steps.map((step) =>
      step.type === type
        ? {
          ...step,
          show:
            greyItems.includes(type) ? RequirementConfirmShowEnum.HIDDEN
              : step.show === RequirementConfirmShowEnum.SHOW
                ? RequirementConfirmShowEnum.HIDDEN
                : RequirementConfirmShowEnum.SHOW,
        }
        : step
    );

    setSteps(updatedSteps);
    onChange?.(updatedSteps);
  };

  return (
    <div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="steps" direction="horizontal">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}
            >
              {steps
                .sort((a, b) => a.sort - b.sort)
                .map((step, index) => (
                  <Draggable
                    isDragDisabled={disableItems.includes(step.type)}
                    key={step.type}
                    draggableId={step.type.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...(disableItems.includes(step.type) ? {} : provided.dragHandleProps)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "8px",
                          backgroundColor: "#f9f9f9",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          ...provided.draggableProps.style,
                        }}
                      >
                        <Checkbox
                          disabled={disableItems.includes(step.type) || greyItems.includes(step.type)}
                          checked={step.show === RequirementConfirmShowEnum.SHOW && !greyItems.includes(step.type)}
                          onChange={() => toggleShow(step.type)}
                          style={{ marginRight: "8px" }}
                        />
                        <Tag
                          color={
                            greyItems.includes(step.type) ? "red" : step.show === RequirementConfirmShowEnum.SHOW ? "green" : "red"
                          }
                          style={{ marginRight: "8px" }}
                        >
                          {step.name}
                        </Tag>
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default StepsFormItem;